import psHttp from '@patsnap/common-ps-http';
import { USER_SERVICE_URI, DRAFTING_URI } from '@/constants/base-url';
import type {
    CreateCheckoutSessionRequest,
    CheckoutSessionResponse,
} from '@/features/pricing/types/payment.types';

/**
 * 订阅项目接口
 */
export interface PaymentSubscriptionItem {
    product_name: string;
    price_id: string;
    amount?: number;
    interval_type?: string;
    [key: string]: any;
}

/**
 * 订阅信息接口
 */
export interface PaymentSubscription {
    subscription_id: string;
    subscription_status: string;
    created_at: string;
    current_period_start: string;
    current_period_end: string;
    cancel_at?: string | null;
    cancel_at_period_end?: boolean;
    items: PaymentSubscriptionItem[];
}

export interface CancelSubscriptionRequest {
    subscription_id: string;
    reason: string;
    remark?: string;
}

/**
 * 创建Stripe支付会话
 * @param data 支付会话参数
 * @returns 支付会话信息（包含checkout URL）
 */
export async function createCheckoutSessionApi(
    data: CreateCheckoutSessionRequest,
): Promise<CheckoutSessionResponse | null> {
    const response = await psHttp<any>({
        url: `${USER_SERVICE_URI}/payment/stripe/checkout-session`,
        method: 'POST',
        data,
    });

    // 兼容两种响应格式：
    // 1. 直接返回 { customer_id, url }
    // 2. 包装在 { code, message, data: { customer_id, url } }
    const rawData = response.data;
    if (rawData?.url) {
        return rawData as CheckoutSessionResponse;
    }
    return rawData?.data || null;
}

/**
 * 获取当前租户用户的所有订阅信息
 */
export async function getSubscriptionsApi(): Promise<PaymentSubscription[]> {
    const response = await psHttp<any>({
        url: `${USER_SERVICE_URI}/payment/tenant-user/subscriptions`,
        method: 'GET',
    });

    // 接口返回订阅数组
    const rawData = response.data;
    if (Array.isArray(rawData)) {
        return rawData;
    }
    return [];
}

/**
 * 取消 Stripe 订阅
 */
export async function cancelSubscriptionApi(data: CancelSubscriptionRequest): Promise<void> {
    await psHttp({
        url: `${USER_SERVICE_URI}/payment/stripe/cancel-subscription`,
        method: 'POST',
        data,
    });
}

/**
 * 上报支付成功到 s-drafting 后端
 */
export interface ReportPaymentSuccessData {
    user_email?: string;
    plan_type: string;
    amount: number;
    currency?: string;
    billing_cycle: string;
    stripe_price_id?: string;
}

export function reportPaymentSuccessApi(data: ReportPaymentSuccessData) {
    return psHttp<any>({
        url: `${DRAFTING_URI}/api/payment`,
        method: 'POST',
        data,
        transformCode: true,
        headers: { 'x-api-version': '1.0' },
    }).then(({ data }) => data.data);
}
